import { SxProps, styled } from '@cp/ds';
import { Box } from '@cp/ds/src/components/box';
import { withProps } from '@cp/ds/src/hocs';
import { Typography } from '@cp/ds/src/components/typography';
import { Stack } from '@cp/ds/src/components/stack';
import { DealRejectionReasonType, DealStatus } from '@common/model/deal';
import { useFormContext } from 'react-hook-form';
import { MouseEventHandler, useEffect, useState } from 'react';
import { omit } from 'lodash';
import { ModerationStatus } from '@common/model/moderation/moderationStatus';
import { adminCatalogFeedbackApi } from 'src/entities/catalog/feedback';
import { statuses2CountersType } from '@common/model/processing';
import { TypeOfItems } from '../../lib/config';
import { ProcessingAndReviewsFiltersType } from '../processingAndReviews';
import { useProcessingCounters } from '../processingCountersProvider';
import { ProcessingAndReviewsMapper } from '../../model/mapper';

interface ItemProps {
  sx?: SxProps;
  label: string;
  data: Partial<ProcessingAndReviewsFiltersType>;
}

const dependentKeys: Array<keyof ProcessingAndReviewsFiltersType> = ['dealStatus', 'typeOfItems', 'moderationStatus', 'rejectionReason'];

const Button = withProps(
  styled(Box)<{ isActive?: boolean }>(({ theme, isActive }) => ({
    padding: '8px 12px',
    background: isActive ? theme.palette['brand pink'] : theme.palette.white,
    cursor: isActive ? 'default' : 'pointer',
    pointerEvents: isActive ? 'none' : 'all',
    borderColor: theme.palette['gray middle'],
    borderWidth: 1,
    borderStyle: 'solid',
    color: isActive ? theme.palette.white : theme.palette.gray,
  })),
  {
    component: 'button',
  },
);

const checkIsActive = (data: Partial<ProcessingAndReviewsFiltersType>, form: ProcessingAndReviewsFiltersType) => {
  if (data.typeOfItems !== form.typeOfItems) {
    return false;
  }
  if (form.dealStatus && data.dealStatus === form.dealStatus) {
    if (form.dealStatus === +DealStatus.Rejected) {
      return data.rejectionReason === form.rejectionReason;
    }

    return true;
  }

  if (form.moderationStatus && data.moderationStatus === form.moderationStatus) {
    return true;
  }

  return false;
};

const Item = ({ data, label, sx }: ItemProps) => {
  const { counters } = useProcessingCounters();
  const form = useFormContext<ProcessingAndReviewsFiltersType>();
  const [isActive, setIsActive] = useState(checkIsActive(data, form.getValues()));
  const { data: feedbacksData } = adminCatalogFeedbackApi.endpoints.adminFeedbacks.useQuery(
    ProcessingAndReviewsMapper.toFeedbacksQuery({ ...form.getValues(), ...data }, true),
    {
      skip: data.typeOfItems !== (TypeOfItems.Review as number),
      refetchOnMountOrArgChange: true,
    },
  );

  const handleChangeItem: MouseEventHandler<HTMLBRElement> = (event) => {
    event.preventDefault();
    form.reset({ ...omit(form.getValues(), ...dependentKeys), ...data, page: 1 });
  };

  useEffect(() => {
    const unsubscribies = dependentKeys.map((name) =>
      form.subscribe({
        name,
        formState: {
          values: true,
        },
        callback({ values }) {
          setIsActive(checkIsActive(data, values));
        },
      }),
    );

    return () => {
      unsubscribies.map((cb) => cb());
    };
  }, [data]);

  const rejectionReason = data?.rejectionReason;
  const dealStatus = data?.dealStatus || DealStatus.Negotiation;
  const counterType = statuses2CountersType(dealStatus, rejectionReason);

  return (
    <Button isActive={isActive} onClick={handleChangeItem} sx={sx}>
      <Stack alignItems="center" flexDirection="row" gap={1} sx={{ width: 'max-content' }}>
        <Typography variant="body bold">
          {label + ' '}
          {data.typeOfItems === TypeOfItems.Processing && `(${counters[counterType].count})`}
          {data.typeOfItems === TypeOfItems.Review && !!feedbacksData && `(${feedbacksData.totalCount})`}
        </Typography>
      </Stack>
    </Button>
  );
};

export const ItemsType = () => {
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Stack flexDirection="row" gap={1.5}>
        <Stack flexDirection="row">
          <Item
            data={{ dealStatus: DealStatus.Negotiation, typeOfItems: TypeOfItems.Processing }}
            label="Новые"
            sx={{ borderTopLeftRadius: 12, borderBottomLeftRadius: 12 }}
          />
          <Item data={{ dealStatus: DealStatus.Accepted, typeOfItems: TypeOfItems.Processing }} label="Приняты" />
          <Item
            data={{
              dealStatus: DealStatus.Rejected,
              typeOfItems: TypeOfItems.Processing,
              rejectionReason: DealRejectionReasonType.ClientDecision,
            }}
            label="Отклонены"
          />
          <Item
            data={{
              dealStatus: DealStatus.Rejected,
              typeOfItems: TypeOfItems.Processing,
              rejectionReason: DealRejectionReasonType.TimeForNegotiationExpired,
            }}
            label="Проигнорированы"
          />
          <Item data={{ dealStatus: DealStatus.InProgress, typeOfItems: TypeOfItems.Processing }} label="Идут" />
        </Stack>
        <Stack flexDirection="row">
          <Item data={{ typeOfItems: TypeOfItems.Processing, dealStatus: DealStatus.Completed }} label="Ждут оценки" />
          <Item data={{ typeOfItems: TypeOfItems.Review, moderationStatus: ModerationStatus.Free }} label="На проверку" />
          <Item data={{ typeOfItems: TypeOfItems.Review, moderationStatus: ModerationStatus.Success }} label="Опубликованы" />
          <Item
            data={{ typeOfItems: TypeOfItems.Review, moderationStatus: ModerationStatus.NotPass }}
            label="Отклонены"
            sx={{ borderTopRightRadius: 12, borderBottomRightRadius: 12 }}
          />
        </Stack>
      </Stack>
    </Box>
  );
};
